import { createFileRoute } from '@tanstack/react-router';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Admin Analytics - Platform-wide analytics and reporting
 */
export const Route = createFileRoute('/_authenticated/admin/analytics')({
  component: AdminAnalytics,
});

function AdminAnalytics() {
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      buttonBg: '#dc2626',
      buttonInactive: 'rgba(220, 38, 38, 0.2)',
      buttonInactiveText: '#d1d5db',
      chartBg: 'rgba(15, 15, 20, 0.6)',
      headerColor: '#ef4444'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      buttonBg: '#dc2626',
      buttonInactive: '#fef2f2',
      buttonInactiveText: '#991b1b',
      chartBg: '#fef2f2',
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.headerColor,
            marginBottom: '0.5rem'
          }}>
            Analytics & Reports
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Platform-wide metrics and performance insights
          </p>
        </div>

        {/* Time Range Selector */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          {['7 days', '30 days', '90 days', 'All time'].map((period) => (
            <button
              key={period}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: period === '30 days' ? c.buttonBg : c.buttonInactive,
                color: period === '30 days' ? 'white' : c.buttonInactiveText,
                border: period === '30 days' ? 'none' : `1px solid ${c.cardBorder}`,
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {period}
            </button>
          ))}
        </div>

        {/* Key Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <MetricCard
            title="Platform Revenue"
            value="$0.00"
            change="+0%"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="New Users"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="Total Transactions"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="Avg. Order Value"
            value="$0.00"
            change="+0%"
            trend="neutral"
            theme={theme}
          />
        </div>

        {/* Charts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <ChartCard title="Revenue Over Time" theme={theme} />
          <ChartCard title="User Growth" theme={theme} />
          <ChartCard title="Product Performance" theme={theme} />
          <ChartCard title="License Activations" theme={theme} />
        </div>

        {/* Top Performers */}
        <div style={{
          background: c.cardBg,
          border: `1px solid ${c.cardBorder}`,
          borderRadius: '0.5rem',
          padding: '1.5rem',
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: c.headerColor,
            marginBottom: '1rem'
          }}>
            Top Performing Products
          </h2>
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: c.textMuted
          }}>
            No data available yet
          </div>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  theme: 'dark' | 'light';
}

function MetricCard({ title, value, change, trend, theme }: MetricCardProps) {
  const trendColor = trend === 'up' ? '#059669' : trend === 'down' ? '#dc2626' : '#6b7280';

  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <h3 style={{
        fontSize: '0.875rem',
        fontWeight: '500',
        color: c.textMuted,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        color: c.text,
        marginBottom: '0.5rem'
      }}>
        {value}
      </p>
      <p style={{
        fontSize: '0.875rem',
        color: trendColor,
        fontWeight: '500'
      }}>
        {change} from previous period
      </p>
    </div>
  );
}

interface ChartCardProps {
  title: string;
  theme: 'dark' | 'light';
}

function ChartCard({ title, theme }: ChartCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      chartBg: 'rgba(15, 15, 20, 0.6)'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      chartBg: '#fef2f2'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <h3 style={{
        fontSize: '1.125rem',
        fontWeight: '600',
        color: c.text,
        marginBottom: '1rem'
      }}>
        {title}
      </h3>
      <div style={{
        height: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: c.chartBg,
        borderRadius: '0.375rem',
        color: c.textMuted
      }}>
        Chart placeholder
      </div>
    </div>
  );
}

export default AdminAnalytics;
